// ownerContactFunds — the Owner Contact form's "Funds & year-end taxes" logic for
// the married-filing-jointly model (docs/appfolio-owner-vendor-sync-spec.md §13.2,
// §13.3). PURE: type-only imports (erased), so it tests under a bare
// `npx tsx --test src/lib/ownerContactFunds.test.ts`.
//
// The SERVER decides the model (applicable.spousesQuestion / taxUnits /
// fundsFollowOwnership) — this module never re-derives tax units. It only turns
// the owner's clicks into the `answers.spouses` shape the server reads, and
// words what the server derived.
//
// Consumer-facing strings here are owner copy: every one is
// // Copy approved by Miles 2026-09-25 (docs/bespoke-communications-doctrine.md).

import type {
  OwnerContactAnswers,
  OwnerContactApplicable,
  OwnerContactApplicableOwner,
  OwnerContactCouple,
  OwnerContactSpouses,
  OwnerTaxUnit,
} from './ownerContactClient';

/**
 * Which funds section the form shows:
 *   • 'contact_only' — the existing-owner override intake: no banking / 1099 section.
 *   • 'legacy'       — a server without the §13 fields, or a single owner: today's
 *                      section, byte-for-byte (the single-owner line is the same in both).
 *   • 'units'        — the §13 model: spouses question, couple designees, and funds
 *                      that follow ownership.
 */
export type FundsFlow = 'legacy' | 'contact_only' | 'units';

export function fundsFlow(
  a: Pick<OwnerContactApplicable, 'ownerCount' | 'showFundsSection' | 'spousesQuestion'>,
): FundsFlow {
  if (a.showFundsSection === false) return 'contact_only';
  if (a.spousesQuestion === undefined) return 'legacy';
  if (a.ownerCount <= 1) return 'legacy';
  return 'units';
}

export interface RosterPerson {
  index: number;
  name: string;
}

type PerOwner = Pick<OwnerContactApplicableOwner, 'ownerIndex' | 'name' | 'kind'>;

/** A person's name as the form shows it (never an id: a positional fallback). */
export function personName(perOwner: readonly PerOwner[], index: number): string {
  const p = perOwner.find((x) => x.ownerIndex === index);
  return p?.name?.trim() || `Owner ${index + 1}`;
}

/** The INDIVIDUAL people — only they can be a married couple. */
export function individualsOf(perOwner: readonly PerOwner[]): RosterPerson[] {
  return perOwner
    .filter((p) => p.kind !== 'entity')
    .map((p) => ({ index: p.ownerIndex, name: p.name?.trim() || `Owner ${p.ownerIndex + 1}` }));
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// ---------------------------------------------------------------------------
// The spouses answer
// ---------------------------------------------------------------------------

export type SpousesChoice = 'yes' | 'no' | null;

/** What the owner answered so far (null = not yet). */
export function spousesChoice(spouses: OwnerContactSpouses | undefined): SpousesChoice {
  if (!spouses?.answered) return null;
  return spouses.couples.length > 0 ? 'yes' : 'no';
}

/** Has the question been settled? A roster it does not apply to is settled already. */
export function spousesSettled(
  a: Pick<OwnerContactApplicable, 'spousesQuestion'>,
  answers: Pick<OwnerContactAnswers, 'spouses'>,
): boolean {
  if (a.spousesQuestion === 'none') return true;
  return answers.spouses?.answered === true;
}

function samePair(c: Pick<OwnerContactCouple, 'a' | 'b'>, a: number, b: number): boolean {
  return (c.a === a && c.b === b) || (c.a === b && c.b === a);
}

/**
 * The two-person question: "yes" makes the roster's two individuals the couple
 * (keeping a designee already chosen for them), "no" records no couples.
 */
export function yesNoSpouses(
  choice: 'yes' | 'no',
  individuals: readonly RosterPerson[],
  current?: OwnerContactSpouses,
): OwnerContactSpouses {
  if (choice === 'no' || individuals.length < 2) return { answered: true, couples: [] };
  const a = individuals[0]!.index;
  const b = individuals[1]!.index;
  const prev = current?.couples.find((c) => samePair(c, a, b));
  const couple: OwnerContactCouple = { a, b };
  if (prev?.designee === a || prev?.designee === b) couple.designee = prev.designee;
  return { answered: true, couples: [couple] };
}

/** One couple row while it is being picked (either side may still be empty). */
export interface CoupleDraft {
  a: number | null;
  b: number | null;
}

export function draftFromSpouses(spouses: OwnerContactSpouses | undefined): CoupleDraft[] {
  return (spouses?.couples ?? []).map((c) => ({ a: c.a, b: c.b }));
}

/**
 * The couple-picking rows → the saved answer: only COMPLETE couples, each person
 * in at most one (first claim wins), a designee kept when the same pair already
 * had one. No complete couple yet → undefined (not answered — the owner said
 * "yes" but has not finished; saving `couples: []` would read as "no").
 */
export function spousesFromDraft(
  draft: readonly CoupleDraft[],
  prev?: OwnerContactSpouses,
): OwnerContactSpouses | undefined {
  const used = new Set<number>();
  const couples: OwnerContactCouple[] = [];
  for (const row of draft) {
    const { a, b } = row;
    if (a === null || b === null || a === b || used.has(a) || used.has(b)) continue;
    used.add(a);
    used.add(b);
    const couple: OwnerContactCouple = { a, b };
    const before = prev?.couples.find((c) => samePair(c, a, b));
    if (before?.designee === a || before?.designee === b) couple.designee = before.designee;
    couples.push(couple);
  }
  return couples.length > 0 ? { answered: true, couples } : undefined;
}

/**
 * Who can be picked for one side of one couple row: every individual not already
 * in another row, and not the person on the other side of this row.
 */
export function coupleChoices(
  draft: readonly CoupleDraft[],
  row: number,
  side: 'a' | 'b',
  individuals: readonly RosterPerson[],
): RosterPerson[] {
  const taken = new Set<number>();
  draft.forEach((d, i) => {
    if (i === row) {
      const other = side === 'a' ? d.b : d.a;
      if (other !== null) taken.add(other);
      return;
    }
    if (d.a !== null) taken.add(d.a);
    if (d.b !== null) taken.add(d.b);
  });
  return individuals.filter((p) => !taken.has(p.index));
}

/** Another couple row only when at least two individuals are still unpaired. */
export function canAddCouple(draft: readonly CoupleDraft[], individuals: readonly RosterPerson[]): boolean {
  const used = new Set<number>();
  for (const d of draft) {
    if (d.a !== null) used.add(d.a);
    if (d.b !== null) used.add(d.b);
  }
  return individuals.filter((p) => !used.has(p.index)).length >= 2;
}

/** Record which spouse of a couple receives its funds + 1099. */
export function withCoupleDesignee(
  spouses: OwnerContactSpouses,
  members: readonly number[],
  designee: number,
): OwnerContactSpouses {
  const [a, b] = members;
  if (a === undefined || b === undefined || (designee !== a && designee !== b)) return spouses;
  return {
    answered: true,
    couples: spouses.couples.map((c) => (samePair(c, a, b) ? { ...c, designee } : c)),
  };
}

// ---------------------------------------------------------------------------
// Copy — Copy approved by Miles 2026-09-25 (owner-facing)
// ---------------------------------------------------------------------------

export const CONTACT_ONLY_FUNDS_LINE =
  'Your banking and year-end tax setup stays as it is with Welcome Home Properties.';

// Copy approved by Miles 2026-09-25 — the page intro for a contact-only intake: the full
// intro promises funds and the 1099 are set up here, which is not true for it.
export function contactOnlyIntro(address: string): string {
  return `Your management agreement${address ? ` for ${address}` : ''} is signed — thank you. Let's confirm how to reach you, and who we can call if we ever can't.`;
}

/**
 * Copy approved by Miles 2026-09-25. With just the two of them on the roster, "you" is
 * exactly the couple being asked about; when an entity is on the roster too, the
 * two people are named so the entity's signer is not asked about their own marriage.
 */
export function spousesQuestionText(
  kind: 'yes_no' | 'pairs',
  individuals: readonly RosterPerson[],
  ownerCount: number,
): string {
  if (kind === 'pairs') return 'Are any of these owners married to each other and filing taxes jointly?';
  if (ownerCount === 2 || individuals.length < 2) return 'Are you married and filing taxes jointly?';
  return `Are ${individuals[0]!.name} and ${individuals[1]!.name} married and filing taxes jointly?`;
}

// Copy approved by Miles 2026-09-25 — why we ask (the genuine benefit, per the doctrine).
export function spousesHelper(kind: 'yes_no' | 'pairs', ownerCount: number): string {
  if (kind === 'yes_no' && ownerCount === 2) {
    return 'If you are, you can choose which of you receives the funds and the year-end 1099.';
  }
  return 'A married couple filing jointly can choose which of them receives the funds and the year-end 1099.';
}

export function coupleDesigneeQuestion(nameA: string, nameB: string): string {
  return `Who should receive the funds and the year-end 1099 for ${nameA} and ${nameB}?`;
}

function pct(n: number): string {
  return `${Number(n.toFixed(2))}%`;
}

/**
 * Copy approved by Miles 2026-09-25. One READ-ONLY funds line per tax unit:
 * "Funds follow ownership: Jane Doe receives 60%." A couple names who it is for,
 * or — before they choose — says the share is theirs together.
 */
export function unitFundsLine(unit: OwnerTaxUnit, nameOf: (index: number) => string): string {
  const share = unit.fundsPercent;
  if (unit.kind === 'couple') {
    const names = joinNames(unit.members.map(nameOf));
    if (unit.designee === null) {
      return share === null
        ? `Funds follow ownership: ${names} receive their combined share together — choose above who receives it.`
        : `Funds follow ownership: ${names} receive ${pct(share)} together — choose above who receives it.`;
    }
    return share === null
      ? `Funds follow ownership: ${nameOf(unit.designee)} receives the combined share for ${names}.`
      : `Funds follow ownership: ${nameOf(unit.designee)} receives ${pct(share)} for ${names}.`;
  }
  const who = nameOf(unit.members[0] ?? unit.designee ?? 0);
  return share === null
    ? `Funds follow ownership: ${who} receives a share once ownership is filled in.`
    : `Funds follow ownership: ${who} receives ${pct(share)}.`;
}

export const FUNDS_FOLLOW_OWNERSHIP_HELPER =
  "Each owner's share of the funds follows their share of ownership. Enter everyone's ownership below and we'll show where the funds go.";

/** Sum the ownership column (a UX aid; the server validates). */
export function ownershipSum(
  owners: OwnerContactAnswers['owners'],
  indices: readonly number[],
): number {
  let sum = 0;
  for (const i of indices) {
    const v = owners?.[i]?.ownershipPercent;
    if (typeof v === 'number' && Number.isFinite(v)) sum += v;
  }
  return Number(sum.toFixed(2));
}

// ---------------------------------------------------------------------------
// What gets POSTed
// ---------------------------------------------------------------------------

/**
 * Under the tax-unit model the SERVER derives the handling mode from the units.
 * A `disbursement.mode` saved earlier (an in-flight intake's old "one of us
 * handles it" / "split" answer) is stale — and the form posts the whole answer
 * set, so re-posting it would read as an explicit choice: a posted 'one_handles'
 * between people who are not a married couple is refused (400). So in the units
 * flow the mode is left out of every post; the account label and everything
 * else ride along untouched. Any other flow posts exactly what it did before.
 */
export function postableAnswers(
  a: Pick<OwnerContactApplicable, 'ownerCount' | 'showFundsSection' | 'spousesQuestion'> | null | undefined,
  answers: OwnerContactAnswers,
): OwnerContactAnswers {
  if (!a || fundsFlow(a) !== 'units' || answers.disbursement?.mode === undefined) return answers;
  const { mode: _stale, ...disbursement } = answers.disbursement;
  void _stale;
  const { disbursement: _old, ...rest } = answers;
  void _old;
  return Object.keys(disbursement).length > 0 ? { ...rest, disbursement } : rest;
}

/**
 * The roster's SHAPE — who is at which index (index, kind, and which signed-EPMA
 * owner each person came from). It changes when a person is merged back, split,
 * added, removed or changes kind — never on a name, email or phone edit. The
 * funds section is keyed by it so its in-progress couple picks (index pairs, held
 * locally until a couple is complete) are dropped exactly when the indices move.
 */
export function rosterShapeKey(
  perOwner: ReadonlyArray<Pick<OwnerContactApplicableOwner, 'ownerIndex' | 'kind' | 'sourceIndex'>>,
): string {
  return perOwner.map((p) => `${p.ownerIndex}:${p.kind}:${p.sourceIndex}`).join('|');
}
